import { jsx as _jsx } from "react/jsx-runtime";
/**
 * ThemeProvider - CSS Custom Property Injection
 *
 * Injects OwnYou design tokens as CSS custom properties at the root level.
 * This enables consistent theming across the application and supports
 * future theme switching (light/dark mode).
 *
 * @see docs/architecture/OwnYou_architecture_v13.md Section 4.3
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { colorTokens } from '../tokens/colors';
import { typographyTokens } from '../tokens/typography';
import { spacingTokens } from '../tokens/spacing';
import { radiusTokens } from '../tokens/radius';
import { AURORA_PALETTE_CHANGE_EVENT, DEFAULT_AURORA_PALETTE, getAuroraCssVariables, getPalette, readStoredPalette, setPalette as persistPalette, } from '../themes/aurora';
/**
 * All CSS custom properties combined
 */
const allTokens = {
    ...colorTokens,
    ...typographyTokens,
    ...spacingTokens,
    ...radiusTokens,
};
const ThemeContext = createContext(null);
/**
 * ThemeProvider injects design tokens as CSS custom properties
 *
 * @example
 * ```tsx
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 * ```
 */
export function ThemeProvider({ children, overrides, palette }) {
    const [paletteId, setPaletteId] = useState(() => palette ?? readStoredPalette());
    useEffect(() => {
        if (palette) {
            setPaletteId(palette);
            return undefined;
        }
        const handlePaletteChange = (event) => {
            const customEvent = event;
            if (customEvent.detail?.paletteId) {
                setPaletteId(customEvent.detail.paletteId);
            }
        };
        window.addEventListener(AURORA_PALETTE_CHANGE_EVENT, handlePaletteChange);
        return () => window.removeEventListener(AURORA_PALETTE_CHANGE_EVENT, handlePaletteChange);
    }, [palette]);
    const setPalette = (nextPaletteId) => {
        persistPalette(nextPaletteId);
        setPaletteId(nextPaletteId);
    };
    const tokens = useMemo(() => ({
        ...allTokens,
        ...getAuroraCssVariables(paletteId),
        ...overrides,
    }), [overrides, paletteId]);
    const style = useMemo(() => {
        const cssVars = {};
        for (const [key, value] of Object.entries(tokens)) {
            cssVars[key] = value;
        }
        return cssVars;
    }, [tokens]);
    const contextValue = useMemo(() => ({ tokens, paletteId, setPalette }), [tokens, paletteId]);
    return (_jsx(ThemeContext.Provider, { value: contextValue, children: _jsx("div", { style: style, className: "ownyou-theme-root", "data-theme": "aurora", "data-palette": paletteId || DEFAULT_AURORA_PALETTE, children: children }) }));
}
/**
 * Hook to access theme tokens
 *
 * @example
 * ```tsx
 * function Component() {
 *   const { tokens } = useTheme();
 *   return <div style={{ color: tokens['--color-primary'] }} />;
 * }
 * ```
 */
export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}
/**
 * Get token value by name
 */
export function getToken(name) {
    return allTokens[name];
}
export function usePalette() {
    const { paletteId, setPalette } = useTheme();
    return {
        paletteId,
        palette: getPalette(paletteId),
        setPalette,
    };
}
//# sourceMappingURL=ThemeProvider.js.map